import React, { useState, useEffect } from 'react'
import * as Routes from 'routes'
import { Modal } from 'reactstrap'

import JobApplicationModalContent from './JobApplicationModalContent'
import JobApplicationCompleteContent from './JobApplicationCompleteContent'
import JobApplicationCommentForm from './JobApplicationCommentForm'
import SessionFetchedEvent from 'javascripts/layouts/v2/SessionFetchedEvent'
import TagManager from 'react-gtm-module'

interface JobApplicationButtonProps {
  handleSaveClicked: VoidFunction;
  handleSaving: boolean;
  jobOfferId: number;
  jobOfferTitle: string;
  jobOfferOpen: boolean;
}

interface ServerResponse {
  error_messages?: string[];
}

const JobApplicationButton: React.FC<JobApplicationButtonProps> = (props) => {
  const [errors, setErrors] = useState<Array<string>>([])
  const [handleSaving, setHandleSaving] = useState<boolean>(false)
  const [jobOfferApplicationExists, setJobOfferApplicationExists] = useState<boolean>(false)
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [favoriteJobOffer, setFavoriteJobOffer] = useState<boolean>(false)
  const [login, setLogin] = useState<boolean>(false)
  const [commentable, setCommentable] = useState<boolean>(false)
  const [csrfToken, setCsrfToken] = useState<string>('')
  const [redirectLogin, setRedirectLogin] = useState<boolean>(false)

  useEffect(() => {
    document.addEventListener('session:fetched', (event: SessionFetchedEvent) => {
      const session = event.detail.session
      const user = session.user

      setCsrfToken(session.csrf_token)
      setFavoriteJobOffer(session.favorite_job_offer_ids.some(id => (id === props.jobOfferId)))

      if (user) {
        setLogin(true)
        setJobOfferApplicationExists(user.applied_job_offer_ids.some(id => (id === props.jobOfferId)))
        setCommentable(user.job_offer_application_commentable)
      }
      if (session.flash.success) {
        setRedirectLogin(true)
      }
    })
    if (redirectLogin) {
      setTimeout(() => {
        commentable ? scrollToForm() : openModal()
      }, 300)
    }
    const scrollToForm = () => {
      const formElement = document.getElementById('job-offer-comment-form')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [redirectLogin, commentable])

  const trackJobOfferApplication = () => {
    const current = document.location.href;
    const referrer = document.referrer;
    referrer.includes('recommended_job_offers') && TagManager.dataLayer({
      dataLayer: {
        event: 'job_offer_application_from_recommended_job_offers',
        event_category: 'Conversion',
        event_label: 'job_offer_application'
      }
    })
    current.includes('from=dashboard_recommended_job_offers') && TagManager.dataLayer({
      dataLayer: {
        event: 'job_offer_application_from_dashboard_recommended_job_offers',
        event_category: 'Conversion',
        event_label: 'job_offer_application'
      }
    })
  }

  const handleSaveClicked = () => {
    setHandleSaving(true)
    void $.ajax({
      type: 'POST',
      url: Routes.job_offer_application_path(props.jobOfferId),
      dataType: 'json',
      contentType: 'application/json',
      headers: { 'X-CSRF-TOKEN': csrfToken },
    }).done(() => {
      trackJobOfferApplication();
      setJobOfferApplicationExists(true)
      setErrors([])
      history.pushState(null, null, Routes.application_completed_job_offer_path(props.jobOfferId))
    }).fail((response) => {
      const json = response.responseJSON as ServerResponse
      setErrors(json.error_messages)
    }).always(() => {
      setHandleSaving(false)
    })
  }

  const handleFavoriteClicked = () => {
    void $.ajax({
      type: favoriteJobOffer ? 'DELETE' : 'PUT',
      url: Routes.job_offer_favorite_path(props.jobOfferId),
      dataType: 'json',
      contentType: 'application/json',
      headers: { 'X-CSRF-TOKEN': csrfToken },
    }).done(() => {
      setFavoriteJobOffer(!favoriteJobOffer)
    })
  }

  const handleSubmit = () => {
    trackJobOfferApplication();
    setJobOfferApplicationExists(true)
    setModalIsOpen(true)
  }

  const openModal = () => setModalIsOpen(true)
  const closeModal = () => setModalIsOpen(false)

  const job_favorite_button = () => {
    return (
      <a className={`favorite-btn ${favoriteJobOffer ? 'active' : ''}`} onClick={handleFavoriteClicked}>
        気になる
      </a>
    )
  }

  const closed = () => {
    return (
      <>
        <p>この案件は募集を終了しました。</p>
        <div className="job-btn-container">
          <div className="job-btn">募集終了</div>
          {job_favorite_button()}
        </div>
      </>
    )
  }

  const registerable = () => {
    return (
      <>
        <div className="job-btn-container">
          <a className="job-btn" href={Routes.new_user_path({ job_offer_id: props.jobOfferId })} id="ga-job_offers_detail-btn-register">
            この案件に応募する
          </a>
          {job_favorite_button()}
        </div>
      </>
    )
  }

  const applied = () => {
    return (
      <>
        <p>事務局からの連絡をお待ちください。</p>
        <div className="job-btn-container">
          <div className="job-btn">応募済み</div>
          {job_favorite_button()}
        </div>

        <Modal isOpen={modalIsOpen} className="interview-modal" centered>
          <JobApplicationCompleteContent
            closeModal={closeModal}
            handleSaving={handleSaving}
          />
        </Modal>
      </>
    )
  }

  const renderCommentable = () => {
    return (
      <>
        <p>応募が集中する場合があります。<br />お早めにご相談ください。</p>
        <div className="job-btn-container">
          <a className="job-btn" href="#job-offer-comment-form">応募フォームへ</a>
          {job_favorite_button()}
        </div>
      </>
    )
  }

  const applicable = () => {
    return (
      <>
        <p>応募が集中する場合があります。<br />お早めにご相談ください。</p>
        <div className="job-btn-container">
          <a className="job-btn" onClick={openModal}>応募する</a>
          {job_favorite_button()}
        </div>
        <Modal isOpen={modalIsOpen} fade={false}>
          <JobApplicationModalContent
            jobOfferTitle={props.jobOfferTitle}
            errors={errors}
            handleSaving={handleSaving}
            closeModal={closeModal}
            handleSaveClicked={handleSaveClicked}
          />
        </Modal>
      </>
    )
  }

  const job_application_button = () => {
    if (!props.jobOfferOpen) {
      return (closed())
    }
    if (!login) {
      return (registerable())
    }
    if (jobOfferApplicationExists) {
      return (applied())
    }
    if (commentable) {
      return (renderCommentable())
    }
    return (applicable())
  }

  const comment_form = () => {
    if (!jobOfferApplicationExists && commentable) {
      return (
        <div id="job-offer-comment-form" className='job-offer-comment-form-wrapper'>
          <a className={`favorite-btn ${favoriteJobOffer ? 'active' : ''}`} onClick={handleFavoriteClicked}>
            気になるリストに追加をする
          </a>
          <JobApplicationCommentForm
            action={Routes.job_offer_application_path(props.jobOfferId)}
            csrfToken={csrfToken}
            handleSubmit={handleSubmit}
          />
        </div>
      )
    }
  }

  return (
    <>
      {comment_form()}
      <div className='job-offer-application-button-container job-offer-application-button-container-fixed' id='js-job-offer-application-button-container'>
        {job_application_button()}
      </div>
      <p>
        応募後、企業からご面談依頼があった場合のみ事務局からご連絡いたします。
        <br />
        応募から
        <span>5営業日以内</span>
        に事務局から連絡がない場合は見送りとなりますのでご了承ください。
      </p>
    </>
  )
}

export default JobApplicationButton
